// ORB Session Pro Engine (Opening Range Breakout & Smart Hybrid Retest)
// Targets:
// 1. Asian Session (Tokyo / HK Open): 08h30 VN Time (01:30 UTC)
// 2. European Session (London Open): 15h00 VN Time (08:00 UTC)
// 3. US Session (NY Open): 20h30 VN Time (13:30 UTC) [Optional]

export function getPipMultiplier(price) {
  if (price < 5.0) return 10000.0;
  if (price < 500.0) return 100.0;
  if (price < 15000.0) return 10.0;
  return 1.0;
}

export function getPriceDecimals(price, sampleBar) {
  if (sampleBar && typeof sampleBar.close === 'number') {
    const str = String(sampleBar.close);
    if (str.includes('.')) {
      const d = str.split('.')[1].length;
      if (d >= 1 && d <= 5) return d;
    }
  }
  if (price < 5.0) return 5;
  if (price < 500.0) return 3;
  if (price < 15000.0) return 2;
  return 2;
}

export function roundPrice(val, dec = 2) {
  if (val === null || val === undefined || isNaN(val)) return val;
  return Number(Number(val).toFixed(dec));
}

export function resampleBars(bars, targetTfMin = 5) {
  if (!bars || bars.length === 0) return [];
  const periodSec = targetTfMin * 60;
  const result = [];
  let current = null;
  let currentBucket = null;

  for (let i = 0; i < bars.length; i++) {
    const b = bars[i];
    const bucket = Math.floor(b.time / periodSec) * periodSec;
    if (bucket !== currentBucket) {
      if (current) result.push(current);
      currentBucket = bucket;
      current = {
        time: bucket,
        open: b.open,
        high: b.high,
        low: b.low,
        close: b.close,
        volume: b.volume || 0,
        originalIndex: i
      };
    } else {
      current.high = Math.max(current.high, b.high);
      current.low = Math.min(current.low, b.low);
      current.close = b.close;
      current.volume += (b.volume || 0);
    }
  }
  if (current) result.push(current);
  return result;
}

export function calcATRSeries(highs, lows, closes, period = 14) {
  const n = closes.length;
  if (n === 0) return [];
  const tr = new Array(n);
  tr[0] = highs[0] - lows[0];
  for (let i = 1; i < n; i++) {
    const hl = highs[i] - lows[i];
    const hc = Math.abs(highs[i] - closes[i - 1]);
    const lc = Math.abs(lows[i] - closes[i - 1]);
    tr[i] = Math.max(hl, hc, lc);
  }
  const atr = new Array(n);
  const p = Math.max(1, period);
  if (n < p) return tr;
  let sumP = 0;
  for (let i = 0; i < p; i++) sumP += tr[i];
  const firstAtr = sumP / p;
  for (let i = 0; i < p; i++) atr[i] = firstAtr;
  let currAtr = firstAtr;
  for (let i = p; i < n; i++) {
    currAtr = (currAtr * (p - 1) + tr[i]) / p;
    atr[i] = currAtr;
  }
  return atr;
}

export function isSummerDst(year, month, day) {
  // month is 1-12. Europe/UK DST runs from last Sunday of March to last Sunday of October.
  if (month >= 4 && month <= 9) return true;
  if (month === 11 || month === 12 || month === 1 || month === 2) return false;
  if (month === 3) {
    let lastSun = 31;
    while (new Date(Date.UTC(year, 2, lastSun)).getUTCDay() !== 0) lastSun--;
    return day >= lastSun;
  }
  if (month === 10) {
    let lastSun = 31;
    while (new Date(Date.UTC(year, 9, lastSun)).getUTCDay() !== 0) lastSun--;
    return day < lastSun;
  }
  return false;
}

export function calculateOrbSessionDataSinglePass(bars, params = {}) {
  const enableAsia = params.orbAsiaEnable !== false;
  const enableLondon = params.orbLondonEnable !== false;
  const enableNY = Boolean(params.orbNyEnable);
  const londonEndVnHour = Number(params.orbLondonEndHour ?? params.orb_london_end_hour ?? 19);
  const londonDuration = Math.max(1, Math.min(8, londonEndVnHour - 15));
  const londonExpireHour = 8 + londonDuration;
  const entryMode = params.orbEntryMode || "SMART_HYBRID";
  const rr = Number(params.orbRr ?? params.r1 ?? 3.0);
  const slBufferMode = params.orbSlBufferMode || params.slBufferMode || "FIXED";
  const slBufferPips = Number(params.orbSlBufferPips ?? params.slBufferPips ?? 2.0);
  const slAtrMult = Number(params.orbSlAtrMult ?? params.slAtrMult ?? 1.0);
  const slBufferMinPips = Number(params.orbSlBufferMinPips ?? params.slBufferMinPips ?? 2.0);
  const slBufferMaxPips = Number(params.orbSlBufferMaxPips ?? params.slBufferMaxPips ?? 15.0);
  const slFilterEnable = Boolean(params.orbSlFilterEnable ?? params.slFilterEnable ?? params.orb_sl_filter_enable);
  const minRiskPips = Number(params.orbMinSlPips ?? params.orb_min_sl_pips ?? params.orbMinRiskPips ?? params.minRiskPips ?? 3.0);
  const maxRiskPips = Number(params.orbMaxSlPips ?? params.orb_max_sl_pips ?? params.orbMaxRiskPips ?? params.maxRiskPips ?? 150.0);
  const maxBreakPips = Number(params.orbMaxBreakPips ?? 15.0);
  const maxLimitBars = Number(params.orbMaxLimitBars ?? params.orb_max_limit_bars ?? 6);
  const cancelOnTp = params.orbCancelOnTp !== false && params.orb_cancel_on_tp !== false;
  
  const rawEmaMode = params.orbEma200Mode ?? params.orb_ema200_mode ?? params.ema200Mode ?? params.ema200_mode;
  const rawEmaFilter = params.orbEma200Filter ?? params.orb_ema200_filter;
  let ema200Mode = "OFF";
  if (rawEmaMode) {
    ema200Mode = String(rawEmaMode).toUpperCase();
  } else if (rawEmaFilter) {
    ema200Mode = "STRICT";
  }

  const maxZonePips = Number(params.orbMaxZonePips ?? 250.0);
  const minZonePips = Number(params.orbMinZonePips ?? 10.0);
  const timeoutExitEnable = params.orbTimeoutExitEnable !== false && params.timeoutExitEnable !== false && params.orb_timeout_exit_enable !== false;

  // Dynamic Limit on large SL (Pullback 40-50% Zone when SL > threshold pips)
  // Strictly isolated to ORB: Never fallback to HMA generic dynamicLimitEnable!
  const dynamicLimitEnable = Boolean(params.orbDynamicLimitEnable ?? params.orb_dynamic_limit_enable);
  const rawLimitThresh = Number(params.orbLimitThresholdPips ?? params.orb_limit_threshold_pips ?? 100.0);
  const limitThresholdPips = Math.max(20.0, rawLimitThresh);
  const rawPullbackPct = Number(params.orbLimitPullbackPct ?? params.orb_limit_pullback_pct ?? 50.0);
  const limitPullbackPct = Math.max(10.0, Math.min(90.0, rawPullbackPct));
  const limitCancelBars = Math.max(1, Number(params.orbLimitCancelBars ?? params.orb_limit_cancel_bars ?? 10));

  // Revert Logic (Đảo Chiều Sau Fakeout / Quét SL)
  const revertEnable = Boolean(params.orbRevertEnable ?? params.orb_revert_enable ?? params.revertEnable ?? params.revert_enable);
  const revertEntryMode = params.orbRevertEntryMode || params.orb_revert_entry_mode || params.revertEntryMode || params.revert_entry_mode || "LIMIT_50_BOX";
  const revertRr = Number(params.orbRevertRr ?? params.orb_revert_rr ?? params.revertRr ?? params.revert_rr ?? 1.5);
  const revertCancelBars = Number(params.orbRevertCancelBars ?? params.orb_revert_cancel_bars ?? params.revertCancelBars ?? params.revert_cancel_bars ?? 6);
  const revertSlBufferPips = Number(params.orbRevertSlBufferPips ?? params.orb_revert_sl_buffer_pips ?? params.revertSlBufferPips ?? params.revert_sl_buffer_pips ?? 2.0);

  // Dynamic Break-Even (Early Break-Even Lock)
  const dynamicBeEnable = Boolean(params.orbDynamicBeEnable ?? params.dynamicBeEnable ?? params.orb_dynamic_be_enable ?? params.dynamic_be_enable);
  const rawBeTrig = Number(params.orbBeTriggerPct ?? params.beTriggerPct ?? params.orb_be_trigger_pct ?? params.be_trigger_pct ?? 75.0);
  const beTriggerPct = rawBeTrig > 1.0 ? rawBeTrig / 100.0 : rawBeTrig;
  const beLockPips = Number(params.orbBeLockPips ?? params.beLockPips ?? params.orb_be_lock_pips ?? params.be_lock_pips ?? 5.0);

  // Timeframe detection: If timeframe is M1, resample to M5 for standard ORB 5m boxes
  const sampleTimeDiff = bars.length >= 2 ? bars[1].time - bars[0].time : 300;
  const isM1 = sampleTimeDiff <= 90;
  const workBars = isM1 ? resampleBars(bars, 5) : bars;

  const samplePrice = workBars[workBars.length - 1]?.close || 1.0;
  const pipMult = getPipMultiplier(samplePrice);
  const decimals = getPriceDecimals(samplePrice, workBars[workBars.length - 1]);
  const beLockPrice = beLockPips / pipMult;
  const revertSlBuf = revertSlBufferPips / pipMult;

  // Precalculate ATR and EMA 200 on work bars
  const closes = workBars.map(b => b.close);
  const highs = workBars.map(b => b.high);
  const lows = workBars.map(b => b.low);
  const atrValues = calcATRSeries(highs, lows, closes, 14);

  const ema200 = [];
  let prevEma = closes[0];
  const k = 2 / 201;
  for (let i = 0; i < closes.length; i++) {
    if (i === 0) prevEma = closes[0];
    else prevEma = closes[i] * k + prevEma * (1 - k);
    ema200.push(prevEma);
  }

  const allTrades = [];
  const chartMarkers = [];
  const sessionBoxes = [];
  const activeSessions = {};

  for (let i = 0; i < workBars.length; i++) {
    const b = workBars[i];
    const d = new Date(b.time * 1000);
    const dayStr = d.toISOString().slice(0, 10);
    const h = d.getUTCHours();
    const m = d.getUTCMinutes();

    // 1. Register Session Opening M5 Candles
    // A. Asian Session: 01:30 UTC = 08:30 VN Time
    if (enableAsia && h === 1 && m === 30) {
      const sessKey = `ASIA_${dayStr}`;
      const boxObj = {
        id: sessKey,
        sessionType: 'ASIA',
        name: 'Phiên Á (08h30 VN)',
        session_name: 'Phiên Á (08h30 VN)',
        day: dayStr,
        time: b.time,
        startTime: b.time,
        endTime: b.time + 6 * 3600,
        end_time: b.time + 6 * 3600,
        high: b.high,
        low: b.low,
        mid: roundPrice((b.high + b.low) / 2.0, decimals),
        open: b.open,
        close: b.close,
        rangePips: Math.round((b.high - b.low) * pipMult * 10) / 10,
        color: '#06b6d4',
        pendingOrder: null,
        pendingRevertOrder: null,
        activeTrade: null,
        tradeDone: false,
        revertDone: false,
        expireHour: 7
      };
      activeSessions[sessKey] = boxObj;
      sessionBoxes.push(boxObj);
    }

    // B. European Session: 08:00 UTC = 15:00 VN Time
    if (enableLondon && h === 8 && m === 0) {
      const sessKey = `LONDON_${dayStr}`;
      const boxObj = {
        id: sessKey,
        sessionType: 'LONDON',
        name: 'Phiên Âu (15h00 VN)',
        session_name: 'Phiên Âu (15h00 VN)',
        day: dayStr,
        time: b.time,
        startTime: b.time,
        endTime: b.time + londonDuration * 3600,
        end_time: b.time + londonDuration * 3600,
        high: b.high,
        low: b.low,
        mid: roundPrice((b.high + b.low) / 2.0, decimals),
        open: b.open,
        close: b.close,
        rangePips: Math.round((b.high - b.low) * pipMult * 10) / 10,
        color: '#f59e0b',
        pendingOrder: null,
        pendingRevertOrder: null,
        activeTrade: null,
        tradeDone: false,
        revertDone: false,
        expireHour: londonExpireHour
      };
      activeSessions[sessKey] = boxObj;
      sessionBoxes.push(boxObj);
    }

    // C. US Session: 13:30 UTC = 20:30 VN Time
    if (enableNY && h === 13 && m === 30) {
      const sessKey = `NY_${dayStr}`;
      const boxObj = {
        id: sessKey,
        sessionType: 'NY',
        name: 'Phiên Mỹ (20h30 VN)',
        session_name: 'Phiên Mỹ (20h30 VN)',
        day: dayStr,
        time: b.time,
        startTime: b.time,
        endTime: b.time + 6 * 3600,
        end_time: b.time + 6 * 3600,
        high: b.high,
        low: b.low,
        mid: roundPrice((b.high + b.low) / 2.0, decimals),
        open: b.open,
        close: b.close,
        rangePips: Math.round((b.high - b.low) * pipMult * 10) / 10,
        color: '#a855f7',
        pendingOrder: null,
        pendingRevertOrder: null,
        activeTrade: null,
        tradeDone: false,
        revertDone: false,
        expireHour: 21
      };
      activeSessions[sessKey] = boxObj;
      sessionBoxes.push(boxObj);
    }

    // 2. Process each active session
    for (const [key, sess] of Object.entries(activeSessions)) {
      if (sess.day !== dayStr && !sess.activeTrade && !sess.pendingRevertOrder && !sess.pendingOrder) {
        delete activeSessions[key];
        continue;
      }

      // Check Active Trade
      if (sess.activeTrade) {
        const t = sess.activeTrade;
        const isBuy = t.action === 'BUY';
        let resolved = false;
        let isRevMarket = false;

        t.current_sl = t.current_sl !== undefined ? t.current_sl : t.sl;
        t.original_sl = t.original_sl !== undefined ? t.original_sl : t.sl;
        t.be_triggered = t.be_triggered || false;

        const tpDistance = Math.abs(t.tp - t.price);
        const triggerDist = (dynamicBeEnable && tpDistance > 0) ? (beTriggerPct * tpDistance) : 999999.0;

        if (isBuy) {
          // 1. Check BE Trigger when price reaches X% TP
          if (!t.be_triggered && dynamicBeEnable) {
            if (b.high >= (t.price + triggerDist)) {
              t.be_triggered = true;
              t.current_sl = Math.max(t.current_sl, roundPrice(t.price + beLockPrice, decimals));
            }
          }

          // 2. Check TP Hit
          if (b.high >= t.tp) {
            t.status = 'WIN_TP';
            t.pnl_diff = t.tp - t.price;
            t.pnl_pips = Math.round(t.pnl_diff * pipMult * 10) / 10;
            t.pnlR = t.target_rr || rr;
            t.win = true;
            resolved = true;
          }
          // 3. Check SL / BE Hit
          else if (b.low <= t.current_sl) {
            if (t.be_triggered && t.current_sl > t.original_sl) {
              t.status = 'BE_WIN';
              t.pnl_diff = Math.round((t.current_sl - t.price) * 1000) / 1000;
              t.pnl_pips = Math.round(beLockPips * 10) / 10;
              t.pnlR = Math.round((t.pnl_diff / (t.risk || 1)) * 10) / 10;
              t.win = true;
            } else {
              t.status = 'LOSS_SL';
              t.pnl_diff = -(t.price - t.original_sl);
              t.pnl_pips = Math.round(t.pnl_diff * pipMult * 10) / 10;
              t.pnlR = -1.0;
              t.win = false;

              // TRIGGER REVERT ON SL LOSS
              const canRevert = revertEnable && !t.is_revert && !sess.revertDone;

              if (canRevert) {
                sess.revertDone = true;
                const revAction = 'SELL';
                const revSl = roundPrice(sess.high + revertSlBuf, decimals);
                isRevMarket = false;
                let revEntry = sess.mid;

                if (revertEntryMode === 'LIMIT_50_BOX') {
                  revEntry = sess.mid;
                } else if (revertEntryMode === 'MARKET') {
                  revEntry = b.close;
                  isRevMarket = true;
                } else if (revertEntryMode === 'LIMIT_50_BREAK') {
                  revEntry = (b.open + b.close) / 2.0;
                } else if (revertEntryMode === 'LIMIT_EDGE') {
                  revEntry = sess.high;
                } else { // SMART_HYBRID
                  const distToBox = Math.abs(b.close - sess.low) * pipMult;
                  if (distToBox <= maxBreakPips) {
                    revEntry = b.close;
                    isRevMarket = true;
                  } else {
                    revEntry = (b.open + b.close) / 2.0;
                  }
                }
                revEntry = roundPrice(revEntry, decimals);

                const revRisk = revSl - revEntry;
                const revRiskPips = Math.round(revRisk * pipMult * 10) / 10;

                if (slFilterEnable && (revRiskPips < minRiskPips || revRiskPips > maxRiskPips)) {
                  // Revert SL rejected by SL distance filter
                  sess.pendingRevertOrder = null;
                  sess.activeTrade = null;
                  sess.revertDone = true;
                  sess.tradeDone = true;
                } else if (revRisk > 0) {
                  const revTp = roundPrice(revEntry - revRisk * revertRr, decimals);
                  const revOrder = {
                    id: `orb_rev_${b.time}`,
                    session: sess.name,
                    session_name: sess.name,
                    sessionType: sess.sessionType,
                    action: revAction,
                    type: revAction,
                    entry_mode: isRevMarket ? 'MARKET' : 'LIMIT',
                    entry_type: `Revert (${revertEntryMode})`,
                    limitPrice: revEntry,
                    price: revEntry,
                    sl: revSl,
                    original_sl: revSl,
                    current_sl: revSl,
                    be_triggered: false,
                    tp: revTp,
                    tp1: revTp,
                    risk: revRisk,
                    risk_pips: revRiskPips,
                    riskPips: revRiskPips,
                    rr_ratio: `1:${revertRr.toFixed(1)}`,
                    target_rr: revertRr,
                    confidence: 85,
                    status: isRevMarket ? 'RUNNING' : 'PENDING_LIMIT',
                    is_revert: true,
                    limit_cancel_bars: revertCancelBars,
                    reasons: [
                      `Đảo chiều Fakeout ${sess.name} (Revert SELL sau khi BUY dính SL)`,
                      `Điểm vào Revert: ${revEntry.toFixed(2)}`
                    ],
                    time: b.time,
                    bar_index: i,
                    box_high: sess.high,
                    box_low: sess.low
                  };

                  if (isRevMarket) {
                    sess.activeTrade = revOrder;
                    sess.revertDone = true;
                    chartMarkers.push({
                      time: b.time,
                      position: 'aboveBar',
                      color: '#f97316',
                      shape: 'arrowDown',
                      text: `REVERT SELL (${b.close.toFixed(2)})`
                    });
                  } else {
                    sess.pendingRevertOrder = revOrder;
                  }
                }
              }
            }
            resolved = true;
          }
        } else {
          // SELL
          // 1. Check BE Trigger when price reaches X% TP
          if (!t.be_triggered && dynamicBeEnable) {
            if (b.low <= (t.price - triggerDist)) {
              t.be_triggered = true;
              t.current_sl = Math.min(t.current_sl, roundPrice(t.price - beLockPrice, decimals));
            }
          }

          // 2. Check TP Hit
          if (b.low <= t.tp) {
            t.status = 'WIN_TP';
            t.pnl_diff = t.price - t.tp;
            t.pnl_pips = Math.round(t.pnl_diff * pipMult * 10) / 10;
            t.pnlR = t.target_rr || rr;
            t.win = true;
            resolved = true;
          }
          // 3. Check SL / BE Hit
          else if (b.high >= t.current_sl) {
            if (t.be_triggered && t.current_sl < t.original_sl) {
              t.status = 'BE_WIN';
              t.pnl_diff = Math.round((t.price - t.current_sl) * 1000) / 1000;
              t.pnl_pips = Math.round(beLockPips * 10) / 10;
              t.pnlR = Math.round((t.pnl_diff / (t.risk || 1)) * 10) / 10;
              t.win = true;
            } else {
              t.status = 'LOSS_SL';
              t.pnl_diff = -(t.original_sl - t.price);
              t.pnl_pips = Math.round(t.pnl_diff * pipMult * 10) / 10;
              t.pnlR = -1.0;
              t.win = false;

              // TRIGGER REVERT ON SL LOSS
              const canRevert = revertEnable && !t.is_revert && !sess.revertDone;

              if (canRevert) {
                sess.revertDone = true;
                const revAction = 'BUY';
                const revSl = roundPrice(sess.low - revertSlBuf, decimals);
                isRevMarket = false;
                let revEntry = sess.mid;

                if (revertEntryMode === 'LIMIT_50_BOX') {
                  revEntry = sess.mid;
                } else if (revertEntryMode === 'MARKET') {
                  revEntry = b.close;
                  isRevMarket = true;
                } else if (revertEntryMode === 'LIMIT_50_BREAK') {
                  revEntry = (b.open + b.close) / 2.0;
                } else if (revertEntryMode === 'LIMIT_EDGE') {
                  revEntry = sess.low;
                } else { // SMART_HYBRID
                  const distToBox = Math.abs(b.close - sess.high) * pipMult;
                  if (distToBox <= maxBreakPips) {
                    revEntry = b.close;
                    isRevMarket = true;
                  } else {
                    revEntry = (b.open + b.close) / 2.0;
                  }
                }
                revEntry = roundPrice(revEntry, decimals);

                const revRisk = revEntry - revSl;
                const revRiskPips = Math.round(revRisk * pipMult * 10) / 10;

                if (slFilterEnable && (revRiskPips < minRiskPips || revRiskPips > maxRiskPips)) {
                  // Revert SL rejected by SL distance filter
                  sess.pendingRevertOrder = null;
                  sess.activeTrade = null;
                  sess.revertDone = true;
                  sess.tradeDone = true;
                } else if (revRisk > 0) {
                  const revTp = roundPrice(revEntry + revRisk * revertRr, decimals);
                  const revOrder = {
                    id: `orb_rev_${b.time}`,
                    session: sess.name,
                    session_name: sess.name,
                    sessionType: sess.sessionType,
                    action: revAction,
                    type: revAction,
                    entry_mode: isRevMarket ? 'MARKET' : 'LIMIT',
                    entry_type: `Revert (${revertEntryMode})`,
                    limitPrice: revEntry,
                    price: revEntry,
                    sl: revSl,
                    original_sl: revSl,
                    current_sl: revSl,
                    be_triggered: false,
                    tp: revTp,
                    tp1: revTp,
                    risk: revRisk,
                    risk_pips: revRiskPips,
                    riskPips: revRiskPips,
                    rr_ratio: `1:${revertRr.toFixed(1)}`,
                    target_rr: revertRr,
                    confidence: 85,
                    status: isRevMarket ? 'RUNNING' : 'PENDING_LIMIT',
                    is_revert: true,
                    limit_cancel_bars: revertCancelBars,
                    reasons: [
                      `Đảo chiều Fakeout ${sess.name} (Revert BUY sau khi SELL dính SL)`,
                      `Điểm vào Revert: ${revEntry.toFixed(decimals)}`
                    ],
                    time: b.time,
                    bar_index: i,
                    box_high: sess.high,
                    box_low: sess.low
                  };

                  if (isRevMarket) {
                    sess.activeTrade = revOrder;
                    sess.revertDone = true;
                    chartMarkers.push({
                      time: b.time,
                      position: 'belowBar',
                      color: '#f97316',
                      shape: 'arrowUp',
                      text: `REVERT BUY (${b.close.toFixed(2)})`
                    });
                  } else {
                    sess.pendingRevertOrder = revOrder;
                  }
                }
              }
            }
            resolved = true;
          }
        }

        // Session timeout exit
        if (timeoutExitEnable && !resolved && (h >= sess.expireHour && sess.day === dayStr)) {
          t.status = 'SESSION_CLOSE';
          t.pnl_diff = isBuy ? b.close - t.price : t.price - b.close;
          t.pnl_pips = Math.round(t.pnl_diff * pipMult * 10) / 10;
          t.pnlR = Math.round((t.pnl_diff / (t.risk || 1)) * 10) / 10;
          t.win = t.pnl_pips > 0;
          resolved = true;
        }

        if (resolved) {
          t.exit_price = b.close;
          t.exit_time = b.time;
          allTrades.push(t);
          if (t.is_revert) {
            // Khi lệnh Revert kết thúc (TP hoặc SL): Khóa phiên vĩnh viễn, "dính SL thì thôi"
            sess.activeTrade = null;
            sess.pendingRevertOrder = null;
            sess.tradeDone = true;
            sess.revertDone = true;
          } else if (!isRevMarket) {
            sess.activeTrade = null;
            if (!sess.pendingRevertOrder) {
              sess.tradeDone = true;
            }
          }
        }
        continue;
      }

      // Check Pending Revert Order
      if (sess.pendingRevertOrder) {
        const p = sess.pendingRevertOrder;
        const isBuy = p.action === 'BUY';
        let filled = false;

        // Check Limit Fill
        if (isBuy && b.low <= p.limitPrice) filled = true;
        else if (!isBuy && b.high >= p.limitPrice) filled = true;

        const barsWaiting = i - (p.bar_index !== undefined ? p.bar_index : i);
        if (filled) {
          sess.activeTrade = {
            ...p,
            price: p.limitPrice,
            fill_bar: i,
            order_time: p.time || b.time,
            fill_time: b.time,
            time: p.time || b.time,
            current_sl: p.sl,
            original_sl: p.sl,
            be_triggered: false,
            status: 'RUNNING'
          };
          sess.pendingRevertOrder = null;
          sess.revertDone = true;

          chartMarkers.push({
            time: b.time,
            position: isBuy ? 'belowBar' : 'aboveBar',
            color: '#f97316',
            shape: isBuy ? 'arrowUp' : 'arrowDown',
            text: `REVERT ${p.action} (Fill: ${p.limitPrice.toFixed(2)})`
          });
        } else if (barsWaiting >= revertCancelBars || (h >= sess.expireHour && sess.day === dayStr)) {
          // Lệnh Pending Revert không khớp bị hủy, không tạo trade ảo
          sess.pendingRevertOrder = null;
          sess.tradeDone = true;
          sess.revertDone = true;
        }
        continue;
      }

      // Check Pending Limit Order
      if (sess.pendingOrder) {
        const p = sess.pendingOrder;
        const isBuy = p.action === 'BUY';
        let filled = false;

        // A. Invalidation: Price hit TP before filling limit order
        if (cancelOnTp) {
          if ((isBuy && b.high >= p.tp) || (!isBuy && b.low <= p.tp)) {
            sess.pendingOrder = null;
            sess.tradeDone = true;
            continue;
          }
        }

        // B. Check Limit Fill
        if (isBuy && b.low <= p.limitPrice) filled = true;
        else if (!isBuy && b.high >= p.limitPrice) filled = true;

        const barsWaiting = i - (p.bar_index !== undefined ? p.bar_index : i);
        if (filled) {
          sess.activeTrade = {
            ...p,
            price: p.limitPrice,
            fill_bar: i,
            time: b.time,
            current_sl: p.sl,
            original_sl: p.sl,
            be_triggered: false,
            status: 'RUNNING'
          };
          sess.pendingOrder = null;

          chartMarkers.push({
            time: b.time,
            position: isBuy ? 'belowBar' : 'aboveBar',
            color: isBuy ? '#10b981' : '#ef4444',
            shape: isBuy ? 'arrowUp' : 'arrowDown',
            text: `ORB ${p.action} (Fill: ${p.limitPrice.toFixed(2)})`
          });
        } else if (barsWaiting >= (p.is_dynamic_limit && p.limit_cancel_bars ? Number(p.limit_cancel_bars) : maxLimitBars) || (h >= sess.expireHour && sess.day === dayStr)) {
          // Lệnh Pending Limit không khớp bị hủy, không tạo trade ảo
          sess.pendingOrder = null;
          sess.tradeDone = true;
        }
        continue;
      }

      // Scan for Breakout from this session's zone
      if (! (h >= sess.expireHour && sess.day === dayStr) && !sess.tradeDone && !sess.activeTrade && !sess.pendingOrder && !sess.pendingRevertOrder) {
        if (sess.rangePips < minZonePips || sess.rangePips > maxZonePips) {
          sess.tradeDone = true;
          continue;
        }

        // Dynamic SL Buffer computation (Fixed vs ATR)
        let currentBufPips = slBufferPips;
        if (slBufferMode === "ATR" && atrValues.length > i) {
          const candleAtrPips = (atrValues[i] || (b.high - b.low)) * pipMult;
          currentBufPips = Math.max(slBufferMinPips, Math.min(slBufferMaxPips, candleAtrPips * slAtrMult));
        }
        const slBuffer = currentBufPips / pipMult;

        // Breakout DOWN (SELL)
        if (b.close < sess.low && workBars[i - 1].close >= sess.low) {
          const isAboveEma = b.close > ema200[i];
          if (ema200Mode === 'STRICT' && isAboveEma) continue;

          const breakDistPips = (sess.low - b.close) * pipMult;
          let entryPrice = sess.low;
          let isMarket = false;

          if (entryMode === 'MARKET_BREAK') {
            entryPrice = b.close;
            isMarket = true;
          } else if (entryMode === 'LIMIT_EDGE') {
            entryPrice = sess.low;
            isMarket = false;
          } else if (entryMode === 'LIMIT_50_BREAK') {
            entryPrice = (b.open + b.close) / 2;
            isMarket = false;
          } else {
            // SMART_HYBRID
            if (breakDistPips <= maxBreakPips) {
              entryPrice = b.close;
              isMarket = true;
            } else {
              entryPrice = (b.open + b.close) / 2;
              isMarket = false;
            }
          }

          entryPrice = roundPrice(entryPrice, decimals);
          const sl = roundPrice(sess.high + slBuffer, decimals);
          let risk = roundPrice(sl - entryPrice, decimals);
          let riskPips = Math.round(risk * pipMult * 10) / 10;

          // Original Hybrid TP Target based on original entry and original risk
          const origEntryPrice = entryPrice;
          const origRisk = risk;
          const origTp = roundPrice(origEntryPrice - rr * origRisk, decimals);

          // Dynamic Limit on large breakout SL (Pullback 40-50% Zone)
          const initialRiskPips = riskPips;
          let isDynamicLimit = false;
          if (dynamicLimitEnable && initialRiskPips > limitThresholdPips) {
            isMarket = false;
            isDynamicLimit = true;
            const pct = limitPullbackPct / 100.0;
            entryPrice = roundPrice(b.close + pct * (sl - b.close), decimals);
            risk = roundPrice(sl - entryPrice, decimals);
            riskPips = Math.round(risk * pipMult * 10) / 10;
          }

          // SL Filter: Check min & max allowed SL distance
          if (slFilterEnable && (riskPips < minRiskPips || riskPips > maxRiskPips)) {
            continue;
          }

          // TP giữ theo RR gốc của hệ Hybrid đánh (không co lại theo risk mới)
          const tp = isDynamicLimit ? origTp : roundPrice(entryPrice - rr * risk, decimals);
          const actualRr = (isDynamicLimit && risk > 0) ? (Math.abs(tp - entryPrice) / risk) : rr;

          const reasons = [
            `Phá vỡ biên dưới ${sess.name} (${sess.low.toFixed(decimals)})` + (isAboveEma ? " [Ngược EMA 200]" : " [Thuận EMA 200]"),
            `Biên độ phiên: ${sess.rangePips.toFixed(1)} pips | R:R 1:${rr.toFixed(1)}`
          ];
          if (isDynamicLimit) {
            reasons.unshift(`⚡ SL lớn (${initialRiskPips.toFixed(1)}p > ${limitThresholdPips.toFixed(1)}p): Chuyển Limit hồi ${Math.round(limitPullbackPct)}% Zone tại ${entryPrice.toFixed(decimals)} (TP giữ theo RR gốc ${origTp.toFixed(decimals)}, R:R đạt 1:${actualRr.toFixed(1)})`);
          } else if (isMarket) {
            reasons.push(`Vào Market nến ngắn (${breakDistPips.toFixed(1)} pips)`);
          } else {
            reasons.push(`Đặt Limit hồi 50% (${entryPrice.toFixed(decimals)})`);
          }

          const orderData = {
            id: `orb_${b.time}`,
            session: sess.name,
            session_name: sess.name,
            sessionType: sess.sessionType,
            action: 'SELL',
            type: 'SELL',
            entry_mode: isMarket ? 'MARKET' : 'LIMIT',
            entry_type: isDynamicLimit ? 'Dynamic Limit Retest' : (isMarket ? 'Market Breakout' : 'Limit Retest'),
            limitPrice: entryPrice,
            price: entryPrice,
            sl,
            original_sl: sl,
            current_sl: sl,
            be_triggered: false,
            tp,
            tp1: tp,
            risk,
            riskPips,
            risk_pips: riskPips,
            rr_ratio: `1:${actualRr.toFixed(1)}`,
            confidence: 88,
            status: isMarket ? 'RUNNING' : 'PENDING_LIMIT',
            limit_cancel_bars: isDynamicLimit ? limitCancelBars : maxLimitBars,
            is_counter_trend: isAboveEma,
            reasons: reasons,
            time: b.time,
            bar_index: i,
            box_high: sess.high,
            box_low: sess.low,
            is_dynamic_limit: isDynamicLimit
          };

          if (isMarket) {
            sess.activeTrade = orderData;
            chartMarkers.push({
              time: b.time,
              position: 'aboveBar',
              color: '#ef4444',
              shape: 'arrowDown',
              text: `ORB SELL (Fill: ${b.close.toFixed(decimals)})`
            });
          } else {
            sess.pendingOrder = orderData;
          }
        }
        // Breakout UP (BUY)
        else if (b.close > sess.high && workBars[i - 1].close <= sess.high) {
          const isBelowEma = b.close < ema200[i];
          if (ema200Mode === 'STRICT' && isBelowEma) continue;

          const breakDistPips = (b.close - sess.high) * pipMult;
          let entryPrice = sess.high;
          let isMarket = false;

          if (entryMode === 'MARKET_BREAK') {
            entryPrice = b.close;
            isMarket = true;
          } else if (entryMode === 'LIMIT_EDGE') {
            entryPrice = sess.high;
            isMarket = false;
          } else if (entryMode === 'LIMIT_50_BREAK') {
            entryPrice = (b.open + b.close) / 2;
            isMarket = false;
          } else {
            // SMART_HYBRID
            if (breakDistPips <= maxBreakPips) {
              entryPrice = b.close;
              isMarket = true;
            } else {
              entryPrice = (b.open + b.close) / 2;
              isMarket = false;
            }
          }

          entryPrice = roundPrice(entryPrice, decimals);
          const sl = roundPrice(sess.low - slBuffer, decimals);
          let risk = roundPrice(entryPrice - sl, decimals);
          let riskPips = Math.round(risk * pipMult * 10) / 10;

          // Original Hybrid TP Target based on original entry and original risk
          const origEntryPrice = entryPrice;
          const origRisk = risk;
          const origTp = roundPrice(origEntryPrice + rr * origRisk, decimals);

          // Dynamic Limit on large breakout SL (Pullback 40-50% Zone)
          const initialRiskPips = riskPips;
          let isDynamicLimit = false;
          if (dynamicLimitEnable && initialRiskPips > limitThresholdPips) {
            isMarket = false;
            isDynamicLimit = true;
            const pct = limitPullbackPct / 100.0;
            entryPrice = roundPrice(b.close + pct * (sl - b.close), decimals);
            risk = roundPrice(entryPrice - sl, decimals);
            riskPips = Math.round(risk * pipMult * 10) / 10;
          }

          // SL Filter: Check min & max allowed SL distance
          if (slFilterEnable && (riskPips < minRiskPips || riskPips > maxRiskPips)) {
            continue;
          }

          // TP giữ theo RR gốc của hệ Hybrid đánh (không co lại theo risk mới)
          const tp = isDynamicLimit ? origTp : roundPrice(entryPrice + rr * risk, decimals);
          const actualRr = (isDynamicLimit && risk > 0) ? (Math.abs(tp - entryPrice) / risk) : rr;

          const reasons = [
            `Phá vỡ biên trên ${sess.name} (${sess.high.toFixed(decimals)})` + (isBelowEma ? " [Ngược EMA 200]" : " [Thuận EMA 200]"),
            `Biên độ phiên: ${sess.rangePips.toFixed(1)} pips | R:R 1:${rr.toFixed(1)}`
          ];
          if (isDynamicLimit) {
            reasons.unshift(`⚡ SL lớn (${initialRiskPips.toFixed(1)}p > ${limitThresholdPips.toFixed(1)}p): Chuyển Limit hồi ${Math.round(limitPullbackPct)}% Zone tại ${entryPrice.toFixed(decimals)} (TP giữ theo RR gốc ${origTp.toFixed(decimals)}, R:R đạt 1:${actualRr.toFixed(1)})`);
          } else if (isMarket) {
            reasons.push(`Vào Market nến ngắn (${breakDistPips.toFixed(1)} pips)`);
          } else {
            reasons.push(`Đặt Limit hồi 50% (${entryPrice.toFixed(decimals)})`);
          }

          const orderData = {
            id: `orb_${b.time}`,
            session: sess.name,
            session_name: sess.name,
            sessionType: sess.sessionType,
            action: 'BUY',
            type: 'BUY',
            entry_mode: isMarket ? 'MARKET' : 'LIMIT',
            entry_type: isDynamicLimit ? 'Dynamic Limit Retest' : (isMarket ? 'Market Breakout' : 'Limit Retest'),
            limitPrice: entryPrice,
            price: entryPrice,
            sl,
            original_sl: sl,
            current_sl: sl,
            be_triggered: false,
            tp,
            tp1: tp,
            risk,
            riskPips,
            risk_pips: riskPips,
            rr_ratio: `1:${actualRr.toFixed(1)}`,
            confidence: 88,
            status: isMarket ? 'RUNNING' : 'PENDING_LIMIT',
            limit_cancel_bars: isDynamicLimit ? limitCancelBars : maxLimitBars,
            is_counter_trend: isBelowEma,
            reasons: reasons,
            time: b.time,
            bar_index: i,
            box_high: sess.high,
            box_low: sess.low,
            is_dynamic_limit: isDynamicLimit
          };

          if (isMarket) {
            sess.activeTrade = orderData;
            chartMarkers.push({
              time: b.time,
              position: 'belowBar',
              color: '#10b981',
              shape: 'arrowUp',
              text: `ORB BUY (Fill: ${b.close.toFixed(decimals)})`
            });
          } else {
            sess.pendingOrder = orderData;
          }
        }
      }
    }
  }

  // Collect any active running trades or pending limit orders from current sessions
  const activeSignalsList = [];
  for (const [key, sess] of Object.entries(activeSessions)) {
    if (sess.activeTrade) {
      const t = sess.activeTrade;
      const isBuy = t.action === 'BUY';
      const currP = roundPrice(workBars[workBars.length - 1]?.close || t.price, decimals);
      const pnlDiff = roundPrice(isBuy ? currP - t.price : t.price - currP, decimals);
      const pnlPips = Math.round(pnlDiff * pipMult * 10) / 10;
      activeSignalsList.push({
        ...t,
        status: 'RUNNING',
        sl: t.current_sl || t.sl,
        pnl_diff: pnlDiff,
        pnl_pips: pnlPips,
        pnlPips: pnlPips,
        current_price: currP,
        box_high: roundPrice(sess.high, decimals),
        box_low: roundPrice(sess.low, decimals),
        session_name: sess.name,
        sessionType: sess.sessionType
      });
    } else if (sess.pendingOrder) {
      const p = sess.pendingOrder;
      activeSignalsList.push({
        ...p,
        status: 'PENDING_LIMIT',
        status_text: 'Chờ Khớp Limit',
        box_high: roundPrice(sess.high, decimals),
        box_low: roundPrice(sess.low, decimals),
        session_name: sess.name,
        sessionType: sess.sessionType
      });
    } else if (sess.pendingRevertOrder) {
      const p = sess.pendingRevertOrder;
      activeSignalsList.push({
        ...p,
        status: 'PENDING_LIMIT',
        status_text: 'Chờ Khớp Revert Limit',
        box_high: roundPrice(sess.high, decimals),
        box_low: roundPrice(sess.low, decimals),
        session_name: sess.name,
        sessionType: sess.sessionType
      });
    }
  }

  return {
    allTrades,
    chartMarkers,
    sessionBoxes,
    activeSignalsList,
    workBars,
    ema200,
    pipMult,
    rr
  };
}

export function calculateOrbSessionData(bars, params = {}) {
  const emptyStats = { total_trades: 0, win_rate: 0, wins: 0, losses: 0, net_pnl: 0, profit_factor: 0, avg_rr: "1:3.0" };
  if (!bars || bars.length < 30) {
    return {
      signals: [],
      recent_signals: [],
      all_signals: [],
      latest_signal: null,
      latestSignal: null,
      chart_markers: [],
      chartMarkers: [],
      session_boxes: [],
      sessionBoxes: [],
      market_bias: "NEUTRAL",
      streaks: { max_win_streak: 0, max_loss_streak: 0 },
      backtest: emptyStats,
      backtest_windows: { today: emptyStats, "24h": emptyStats, "3d": emptyStats, "7d": emptyStats, "30d": emptyStats, all: emptyStats }
    };
  }

  const rawEmaMode = params.orbEma200Mode ?? params.orb_ema200_mode ?? params.ema200Mode ?? params.ema200_mode;
  const rawEmaFilter = params.orbEma200Filter ?? params.orb_ema200_filter;
  let ema200Mode = "OFF";
  if (rawEmaMode) {
    ema200Mode = String(rawEmaMode).toUpperCase();
  } else if (rawEmaFilter) {
    ema200Mode = "STRICT";
  }

  const rr = Number(params.orbRr ?? params.r1 ?? 3.0);
  let allTrades = [];
  let chartMarkers = [];
  let sessionBoxes = [];
  let activeSignalsList = [];
  let workBars = [];
  let ema200 = [];
  let pipMult = 1.0;

  if (ema200Mode === 'DUAL_TREND') {
    // Run both OFF and STRICT modes and merge the trades & markers
    const resOff = calculateOrbSessionDataSinglePass(bars, { ...params, orbEma200Mode: 'OFF' });
    const resStrict = calculateOrbSessionDataSinglePass(bars, { ...params, orbEma200Mode: 'STRICT' });

    // Hợp nhất 2 pass DUAL_TREND: Mỗi phiên chỉ gắn kết với 1 pass duy nhất được kích hoạt đầu tiên
    const sessionChosenPass = {};
    const allSessionTrades = [
      ...resOff.allTrades.map(t => ({ ...t, _pass: 'OFF' })),
      ...resStrict.allTrades.map(t => ({ ...t, _pass: 'STRICT' })),
      ...resOff.activeSignalsList.map(t => ({ ...t, _pass: 'OFF' })),
      ...resStrict.activeSignalsList.map(t => ({ ...t, _pass: 'STRICT' }))
    ].sort((a, b) => (a.time || 0) - (b.time || 0));

    for (const t of allSessionTrades) {
      const sessTime = t.time || 0;
      const dStr = new Date(sessTime * 1000).toISOString().slice(0, 10);
      const sessKey = `${t.sessionType || t.session_name || 'sess'}_${dStr}`;
      if (!sessionChosenPass[sessKey]) {
        sessionChosenPass[sessKey] = t._pass;
      }
    }

    for (const t of resOff.allTrades) {
      const sessTime = t.time || 0;
      const dStr = new Date(sessTime * 1000).toISOString().slice(0, 10);
      const sessKey = `${t.sessionType || t.session_name || 'sess'}_${dStr}`;
      if (sessionChosenPass[sessKey] === 'OFF') allTrades.push(t);
    }
    for (const t of resStrict.allTrades) {
      const sessTime = t.time || 0;
      const dStr = new Date(sessTime * 1000).toISOString().slice(0, 10);
      const sessKey = `${t.sessionType || t.session_name || 'sess'}_${dStr}`;
      if (sessionChosenPass[sessKey] === 'STRICT') allTrades.push(t);
    }
    allTrades.sort((a, b) => (a.time || 0) - (b.time || 0));

    for (const a of resOff.activeSignalsList) {
      const sessTime = a.time || 0;
      const dStr = new Date(sessTime * 1000).toISOString().slice(0, 10);
      const sessKey = `${a.sessionType || a.session_name || 'sess'}_${dStr}`;
      if (sessionChosenPass[sessKey] === 'OFF') activeSignalsList.push(a);
    }
    for (const a of resStrict.activeSignalsList) {
      const sessTime = a.time || 0;
      const dStr = new Date(sessTime * 1000).toISOString().slice(0, 10);
      const sessKey = `${a.sessionType || a.session_name || 'sess'}_${dStr}`;
      if (sessionChosenPass[sessKey] === 'STRICT') activeSignalsList.push(a);
    }
    activeSignalsList.sort((a, b) => (a.time || 0) - (b.time || 0));

    // Đồng bộ Chart Markers khớp 1-1 với lệnh hợp lệ
    const validTradeKeys = new Set();
    for (const t of [...allTrades, ...activeSignalsList]) {
      const fillTime = t.fill_time || t.time;
      if (fillTime) {
        validTradeKeys.add(`${fillTime}_${t.action}_${Boolean(t.is_revert)}`);
      }
    }

    const combinedMarkers = [...resOff.chartMarkers, ...resStrict.chartMarkers].sort((a, b) => (a.time || 0) - (b.time || 0));
    const markerSeen = new Set();
    for (const mk of combinedMarkers) {
      const mKey = `${mk.time}_${mk.text || mk.shape}`;
      if (markerSeen.has(mKey)) continue;
      const isRevertMarker = (mk.text || '').includes('REVERT');
      const isBuyMarker = (mk.text || '').includes('BUY');
      const action = isBuyMarker ? 'BUY' : 'SELL';
      const matchKey = `${mk.time}_${action}_${isRevertMarker}`;
      if (validTradeKeys.has(matchKey)) {
        markerSeen.add(mKey);
        chartMarkers.push(mk);
      }
    }

    sessionBoxes = resOff.sessionBoxes;
    workBars = resOff.workBars;
    ema200 = resOff.ema200;
    pipMult = resOff.pipMult;
  } else {
    const res = calculateOrbSessionDataSinglePass(bars, params);
    allTrades = res.allTrades;
    chartMarkers = res.chartMarkers;
    sessionBoxes = res.sessionBoxes;
    activeSignalsList = res.activeSignalsList;
    workBars = res.workBars;
    ema200 = res.ema200;
    pipMult = res.pipMult;
  }


  // Calculate Backtest Metrics
  const computeStats = (tradesList) => {
    let wins = 0;
    let losses = 0;
    let beWins = 0;
    let timeouts = 0;
    let grossP = 0;
    let grossL = 0;
    let netPips = 0;
    let wMaxWin = 0;
    let wMaxLoss = 0;
    let wCurWin = 0;
    let wCurLoss = 0;

    tradesList.forEach((t) => {
      const pnlPips = t.pnl_pips !== undefined ? t.pnl_pips : (t.pnl_diff || 0) * pipMult;
      if (pnlPips > 0 || t.status === 'WIN_TP' || t.status === 'BE_WIN') {
        wins++;
        if (t.status === 'BE_WIN') beWins++;
        grossP += pnlPips;
        wCurWin++;
        wCurLoss = 0;
        if (wCurWin > wMaxWin) wMaxWin = wCurWin;
      } else if (pnlPips < 0 || t.status === 'LOSS_SL') {
        losses++;
        grossL += Math.abs(pnlPips);
        wCurLoss++;
        wCurWin = 0;
        if (wCurLoss > wMaxLoss) wMaxLoss = wCurLoss;
      } else {
        wCurWin = 0;
        wCurLoss = 0;
        if (t.status === 'TIMEOUT') timeouts++;
      }
      netPips += pnlPips;
    });

    const total = wins + losses;
    const wr = total > 0 ? Math.round((wins / total) * 1000) / 10 : 0;
    const pf = grossL > 0 ? Math.round((grossP / grossL) * 100) / 100 : (grossP > 0 ? 3.0 : 1.0);

    return {
      total_trades: total,
      win_rate: wr,
      wins,
      losses,
      be_wins: beWins,
      timeouts,
      net_pnl: Math.round(netPips * 10) / 10,
      profit_factor: pf,
      avg_rr: `1:${rr.toFixed(1)}`,
      max_win_streak: wMaxWin,
      max_loss_streak: wMaxLoss,
      streaks: { max_win_streak: wMaxWin, max_loss_streak: wMaxLoss },
      trades: tradesList
    };
  };

  // Streaks
  let maxWinStreak = 0;
  let maxLossStreak = 0;
  let curWin = 0;
  let curLoss = 0;
  allTrades.forEach((t) => {
    if (t.status === 'WIN_TP' || t.status === 'BE_WIN') {
      curWin++;
      curLoss = 0;
      if (curWin > maxWinStreak) maxWinStreak = curWin;
    } else if (t.status === 'LOSS_SL') {
      curLoss++;
      curWin = 0;
      if (curLoss > maxLossStreak) maxLossStreak = curLoss;
    }
  });

  // Windows
  const nowMs = Date.now();
  const nowVnDate = new Date(nowMs + 7 * 3600 * 1000);
  const vnYear = nowVnDate.getUTCFullYear();
  const vnMonth = nowVnDate.getUTCMonth();
  const vnDay = nowVnDate.getUTCDate();
  const todayStartTs = Math.floor(Date.UTC(vnYear, vnMonth, vnDay, 0, 0, 0) / 1000) - 7 * 3600;
  const threeDaysStartTs = todayStartTs - 2 * 24 * 3600;
  const sevenDaysStartTs = todayStartTs - 6 * 24 * 3600;
  const thirtyDaysStartTs = todayStartTs - 29 * 24 * 3600;

  const tradesToday = allTrades.filter((t) => t.time >= todayStartTs);
  const trades3d = allTrades.filter((t) => t.time >= threeDaysStartTs);
  const trades7d = allTrades.filter((t) => t.time >= sevenDaysStartTs);
  const trades30d = allTrades.filter((t) => t.time >= thirtyDaysStartTs);

  const backtestWindows = {
    today: computeStats(tradesToday),
    "24h": computeStats(tradesToday),
    "3d": computeStats(trades3d),
    "7d": computeStats(trades7d),
    "30d": computeStats(trades30d),
    all: computeStats(allTrades)
  };

  const allSignalsCombined = [...allTrades, ...activeSignalsList];
  allSignalsCombined.sort((a, b) => (a.time || 0) - (b.time || 0));

  const latestSignal = activeSignalsList.length > 0
    ? activeSignalsList[activeSignalsList.length - 1]
    : (allTrades.length > 0 ? allTrades[allTrades.length - 1] : null);

  // Market Bias
  const lastBar = workBars[workBars.length - 1];
  const lastEma200 = ema200[ema200.length - 1] || (lastBar ? lastBar.close : 0);
  const marketBias = lastBar && lastBar.close > lastEma200 ? "BULLISH" : "BEARISH";

  return {
    signals: allSignalsCombined,
    recent_signals: allSignalsCombined.slice(-20),
    all_signals: allSignalsCombined,
    latest_signal: latestSignal,
    latestSignal,
    chart_markers: chartMarkers,
    chartMarkers,
    session_boxes: sessionBoxes,
    sessionBoxes,
    market_bias: marketBias,
    streaks: { max_win_streak: maxWinStreak, max_loss_streak: maxLossStreak },
    backtest: backtestWindows.all,
    backtest_windows: backtestWindows
  };
}
